import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import {
  initGA,
  setUserId,
  setUserProperties,
  trackEvent,
  trackPageView,
  GA4EventParams,
} from 'utils/analytics';
import { deriveUserProperties } from 'utils/analyticsHelpers';
import { useStorage } from './StorageProvider';

interface AnalyticsContextValue {
  trackEvent: (eventName: string, params?: GA4EventParams) => void;
  trackPageView: (path: string, title?: string) => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue | undefined>(undefined);

const MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID ?? '';

export const AnalyticsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { userProfileData } = useStorage();

  // Initialize GA4 once on mount
  useEffect(() => {
    initGA(MEASUREMENT_ID);

    if (userProfileData?.id) {
      setUserId(String(userProfileData.id));
      setUserProperties(deriveUserProperties(userProfileData));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-sync whenever profile changes (login / logout / profile update)
  useEffect(() => {
    if (!userProfileData) {
      setUserId(null);
      return;
    }
    if (userProfileData?.id) {
      setUserId(String(userProfileData.id));
      setUserProperties(deriveUserProperties(userProfileData));
    }
  }, [userProfileData]);

  return (
    <AnalyticsContext.Provider value={{ trackEvent, trackPageView }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = (): AnalyticsContextValue => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
